import React from 'react';
import { Copy, Edit2, Trash2 } from 'lucide-react';
import { formatDateInZone } from '../../lib/timezone';
import useDisplayZone from '../../hooks/useDisplayZone';
import { VISIBILITY_BADGE } from './SharingPicker';

/**
 * The three groups Settings → Templates & Signatures shows for each kind of
 * item (#955): **Mine**, **Shared with me** (with the owner's name) and
 * **Organisation** (admin-managed, read-only here).
 *
 * Only the owner gets Edit and Delete. Everyone else, org masters included,
 * gets **Make a copy**, which creates a private item they own. The server
 * enforces the same rule (writes by anyone but the owner answer 403).
 */

function Badge({ cls, children, testId }) {
    return (
        <span data-testid={testId} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>
            {children}
        </span>
    );
}

function sharingLabel(item) {
    const people = (item.shared_with || []).length;
    const groups = (item.shared_with_groups || []).length;
    if (item.visibility !== 'shared') return null;
    const parts = [];
    if (people) parts.push(`${people} ${people === 1 ? 'person' : 'people'}`);
    if (groups) parts.push(`${groups} ${groups === 1 ? 'group' : 'groups'}`);
    return parts.length ? `Shared with ${parts.join(' and ')}` : 'Shared with no one yet';
}

/**
 * "Edited by <name> · <date>" when someone other than the owner (an org admin,
 * #978) made the last edit. The owner's own next edit restamps it and the line goes.
 */
function editedByLabel(item, zone) {
    if (!item.updated_by || item.updated_by === item.user_id) return null;
    // The API sends tz-naive UTC timestamps; formatDateInZone reads them as UTC and
    // shows the date in the viewer's profile zone (epic #532).
    const date = formatDateInZone(item.updated_at, { zone });
    return `Edited by ${item.updated_by_name || 'an admin'}${date ? ` · ${date}` : ''}`;
}

function ItemRow({ item, idKey, kind, isOwner, subtitle, extraBadges, onEdit, onDelete, onCopy, copying }) {
    const id = item[idKey];
    const zone = useDisplayZone();
    const editedBy = isOwner ? editedByLabel(item, zone) : null;
    const vis = item.is_admin_template ? 'organization' : (item.visibility === 'shared' ? 'shared' : 'private');
    const badge = VISIBILITY_BADGE[vis];
    const detail = isOwner ? sharingLabel(item) : (item.is_admin_template ? 'Managed by your organisation' : (item.owner_name ? `Shared by ${item.owner_name}` : 'Shared with you'));
    return (
        <li
            data-testid={`${kind}-row-${id}`}
            className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-lg border border-stone-200 bg-white"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-sm font-medium text-stone-900 truncate">{item.name}</p>
                    <Badge cls={badge.cls} testId={`${kind}-visibility-${id}`}>{badge.label}</Badge>
                    {extraBadges}
                </div>
                {subtitle && <p className="text-xs text-stone-500 truncate mt-0.5">{subtitle}</p>}
                {detail && <p className="text-[11px] text-stone-400 truncate mt-0.5" data-testid={`${kind}-detail-${id}`}>{detail}</p>}
                {editedBy && (
                    <p className="inline-flex items-center gap-1 text-[11px] font-medium text-stone-600 mt-0.5" data-testid={`${kind}-edited-by-${id}`}>
                        <Edit2 className="w-3 h-3" aria-hidden="true" /> {editedBy}
                    </p>
                )}
            </div>
            <div className="shrink-0 flex items-center gap-1">
                {isOwner ? (
                    <>
                        <button
                            type="button"
                            onClick={() => onEdit(item)}
                            className="p-1.5 text-stone-500 hover:text-stone-900 rounded hover:bg-stone-100"
                            title="Edit"
                            aria-label={`Edit ${item.name}`}
                            data-testid={`${kind}-edit-${id}`}
                        >
                            <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => onDelete(item)}
                            className="p-1.5 text-stone-500 hover:text-red-600 rounded hover:bg-stone-100"
                            title="Delete"
                            aria-label={`Delete ${item.name}`}
                            data-testid={`${kind}-delete-${id}`}
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </>
                ) : (
                    <button
                        type="button"
                        onClick={() => onCopy(item)}
                        disabled={copying === id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-stone-600 border border-stone-200 rounded-md hover:bg-stone-50 disabled:opacity-50"
                        data-testid={`${kind}-copy-${id}`}
                    >
                        <Copy className="w-3 h-3" /> Make a copy
                    </button>
                )}
            </div>
        </li>
    );
}

export function ItemGroup({ title, description, items, emptyText, testId, renderRow }) {
    return (
        <section data-testid={testId} className="space-y-2">
            <div>
                <h3 className="text-sm font-semibold text-stone-900">
                    {title} <span className="text-stone-400 font-normal">({items.length})</span>
                </h3>
                {description && <p className="text-xs text-stone-500">{description}</p>}
            </div>
            {items.length === 0 ? (
                <p className="text-xs text-stone-400 px-3 py-3 border border-dashed border-stone-200 rounded-lg">{emptyText}</p>
            ) : (
                <ul className="space-y-1.5">{items.map(renderRow)}</ul>
            )}
        </section>
    );
}

export { ItemRow };
